import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { MachineForm } from '@/components/machines/MachineForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { getMachineRequest, updateMachineRequest } from '@/services/machines';
import type { CreateMachineBody, MachineDetail, UpdateMachineBody } from '@/types/machines';

export function MachineEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const detailQuery = useQuery<MachineDetail, ApiError>({
    queryKey: ['machine', id],
    queryFn: () => getMachineRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5 * 60_000,
  });

  const updateMutation = useMutation<MachineDetail, ApiError, { body: UpdateMachineBody }>({
    mutationFn: ({ body }) => updateMachineRequest(id, body),
    onSuccess: (updated) => {
      toast.success(`Machine '${updated.name}' saved`);
      void queryClient.invalidateQueries({ queryKey: ['machines'] });
      void queryClient.invalidateQueries({ queryKey: ['machine', id] });
      void navigate(`/machines/${String(id)}`);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not update machine', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('machines.update')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/machines" replace />;
  }

  function handleSubmit(body: CreateMachineBody | UpdateMachineBody): void {
    setErrorMessage(null);
    if ('machineTypeId' in body) {
      // Edit mode shouldn't deliver the Create shape, but defensively narrow.
      const updateBody: UpdateMachineBody = {
        name: body.name,
        code: body.code,
        customFieldValues: body.customFieldValues,
      };
      updateMutation.mutate({ body: updateBody });
    } else {
      updateMutation.mutate({ body });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Machine</h1>
        <p className="text-muted-foreground">
          The machine type is locked at creation (D-103). Use the Allocate dialog on the detail page
          to change the current site.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{detailQuery.data?.name ?? 'Machine'}</CardTitle>
          <CardDescription>Update the metadata + custom-field values.</CardDescription>
        </CardHeader>
        <CardContent>
          {detailQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="machine-edit-loading">
              Loading machine…
            </p>
          )}
          {detailQuery.isError && (
            <p className="text-sm text-destructive" data-testid="machine-edit-error">
              Failed to load machine.
            </p>
          )}
          {detailQuery.data && (
            <MachineForm
              mode="edit"
              initial={detailQuery.data}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              errorMessage={errorMessage}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
